import { useEffect, useState } from 'react';

import { api } from '../../api';
import { go } from '../../App';
import { usePolled, useLoad } from '../../data';
import { InkHero, Loading, QCard, StatusChip, ago, fmtMoney, formatDay, formatWhen, toLocalIso, untilStart, useUndoToast } from '../../ui';
import { Booking, DECLINE_REASONS } from '../../types';

// Canon 11 F1 — the per-reason decline side-effect. A decline whose reason
// means the DAY is out (not just this job) offers to block that day too, so the
// supplier isn't asked again. The block reuses the availability API; the reason
// stored is the block vocabulary (Eva relays it to the hotel, canon 13). Reasons
// that are about capacity or timing, not the day, offer nothing.
const DECLINE_BLOCKS: Record<string, { label: string; reason: string; range?: boolean }> = {
  already_booked: { label: 'Block this day too?', reason: 'fully_booked' },
  weather: { label: 'Block this day for everyone? Eva relays the reason.', reason: 'weather' },
  // Maintenance keeps a boat out for a stretch, not a day — canon 11 F1 offers a
  // RANGE here (the one case with a `through` date). The block is a loop over the
  // one-day endpoint, so it reinforces the same API, not a new one.
  maintenance: { label: 'Block a range?', reason: 'maintenance', range: true },
};

// The days from `from` to `to` inclusive (YYYY-MM-DD), parsed at UTC noon so the
// day never rolls over an offset. Bounded so a fat-fingered range can't loop far.
function daysFromTo(from: string, to: string): string[] {
  const out: string[] = [];
  let t = Date.parse(`${from}T12:00:00Z`);
  const end = Date.parse(`${to}T12:00:00Z`);
  for (let i = 0; t <= end && i < 92; i++, t += 86_400_000) out.push(new Date(t).toISOString().slice(0, 10));
  return out;
}

// F3's sort, in full (R5): escalated → job starts within 30 minutes →
// earliest job start → longest waiting. Never by value — a queue that
// floats the big job is an auction.
function sortRequests(items: Booking[]): Booking[] {
  const soon = Date.now() + 30 * 60_000;
  return [...items].sort((a, b) => {
    const aEsc = a.chase?.state === 'escalated' ? 0 : 1;
    const bEsc = b.chase?.state === 'escalated' ? 0 : 1;
    if (aEsc !== bEsc) return aEsc - bEsc;
    const aSoon = Date.parse(a.start) < soon ? 0 : 1;
    const bSoon = Date.parse(b.start) < soon ? 0 : 1;
    if (aSoon !== bSoon) return aSoon - bSoon;
    const byStart = Date.parse(a.start) - Date.parse(b.start);
    if (byStart !== 0) return byStart;
    return Date.parse(a.createdAt) - Date.parse(b.createdAt);
  });
}

export type SheetMode = 'answer' | 'decline' | 'propose';

export function Requests({ openId, mode = 'answer' }: { openId?: string; mode?: SheetMode }) {
  // One fetch shape across every screen: the full list, filtered in render.
  const { data, error, reload } = usePolled<{ items: Booking[] }>('/bookings');
  const toast = useUndoToast(reload);
  const items = data
    ? sortRequests(data.items.filter((b) => b.status === 'pending' || b.status === 'requested'))
    : null;

  const answered = (booking: Booking, title: string, sub: string) => {
    toast.show(booking, title, sub);
    go('/requests');
    reload();
  };

  const count = items?.length ?? 0;

  return (
    <div>
      <InkHero
        eyebrow="Waiting on you"
        figure={items === null ? '…' : count}
        unit={count === 1 ? 'request' : 'requests'}
        explain="Ordered by what goes wrong soonest — not by when it arrived."
      />

      {error && items === null && <p className="error-line">{error}</p>}
      {items !== null && count === 0 ? (
        <div className="empty">
          {/* B35: no "this is the good outcome" here — this queue cannot know
              whether any hotel can book them yet; Today says where they stand. */}
          <strong>Nothing needs you</strong>
          <p>When a hotel sends a request, it lands here and buzzes once.</p>
        </div>
      ) : (
        // At most ONE escalated card is visible, ever (F3) — the first after
        // the sort; anything else escalated renders as an ordinary card.
        (items ?? []).map((b, i) => (
          <QueueCard key={b.id} b={b} escalated={i === 0 && b.chase?.state === 'escalated'} />
        ))
      )}

      {openId && <RequestSheet id={openId} mode={mode} onAnswered={answered} />}
      {toast.element}
    </div>
  );
}

function QueueCard({ b, escalated }: { b: Booking; escalated?: boolean }) {
  const ms = untilStart(b.start);
  const soon = ms > 0 && ms < 4 * 3600_000;
  const surfaced = b.chase?.state === 'surfaced';
  const chip = escalated
    ? `Escalated · answer by ${b.answerDeadline ? formatWhen(b.answerDeadline) : 'now'}`
    : surfaced
      ? `Eva is chasing · waiting ${ago(b.createdAt)}`
      : soon
        ? `Starts in ${Math.max(1, Math.round(ms / 3600_000))} h`
        : `Waiting ${ago(b.createdAt)}`;
  return (
    <QCard
      chip={<span className={`uchip ${escalated ? 'escalated' : soon || surfaced ? 'soon' : 'waiting'}`}>{chip}</span>}
      title={b.productName ?? b.productId}
      meta={`${b.hotelName} · ${b.guests.adults + (b.guests.children ?? 0)} guests`}
      amount={fmtMoney(b.supplierAmount)}
      caption="you earn"
      when={formatWhen(b.start)}
      open="Open →"
      escalated={escalated}
      onClick={() => go(`/requests/${b.id}`)}
    />
  );
}

// The request sheet as a web drawer. Three answers, one weight: Accept,
// Another time, Can't do it — decline is never behind an overflow menu.
// Each state is a route (/requests/{id}, /decline, /propose), so browser
// back walks out of a state before it closes the sheet.
function RequestSheet({
  id,
  mode,
  onAnswered,
}: {
  id: string;
  mode: SheetMode;
  onAnswered: (b: Booking, title: string, sub: string) => void;
}) {
  // Polled, not one-shot: "answered in the portal meanwhile → the sheet
  // updates live and shows who answered" (S2's edge).
  const { data: booking, error: loadError } = usePolled<Booking>(`/bookings/${id}`);
  const setMode = (m: SheetMode) => go(m === 'answer' ? `/requests/${id}` : `/requests/${id}/${m}`);
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [blockDay, setBlockDay] = useState(false);
  const [blockThrough, setBlockThrough] = useState(''); // the range end, for maintenance
  const [slot, setSlot] = useState<{ start: string; end: string; label: string } | null>(null);
  const [manualDate, setManualDate] = useState('');
  const [manualTime, setManualTime] = useState('09:00');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // Eva drafts the counter-offer line so the supplier sends a reason, not a
  // bare new time (the "Draft my replies" grant, canon 18; the S7-deferred MED).
  // Prefill once on opening propose, only when the box is empty — it is an
  // editable draft, never a template, and never clobbers what the supplier typed.
  const [notePrefilled, setNotePrefilled] = useState(false);
  useEffect(() => {
    if (mode === 'propose' && !notePrefilled && note === '') {
      setNote("That time's taken, I'm sorry — but I can do the slot below. Hope it still works for your guest.");
      setNotePrefilled(true);
    }
  }, [mode, notePrefilled, note]);

  // The supplier's own blocks around the requested day — so the "free slots"
  // offered are genuinely free (canon 11 F2), and a manually-picked blocked
  // day is caught before it ever reaches the hotel. Availability is the
  // supplier's own calendar; the block API already owns it.
  const availTo = booking
    ? toLocalIso(new Date(Date.parse(booking.start) + 21 * 86_400_000), booking.start.slice(19)).slice(0, 10)
    : '';
  const avail = useLoad<{ blocks: { date: string }[] }>(
    booking ? `/availability?from=${booking.start.slice(0, 10)}&to=${availTo}` : null,
  );
  const blockedDays = new Set((avail.data?.blocks ?? []).map((x) => x.date));

  const close = () => go('/requests');
  const answerable = booking && (booking.status === 'pending' || booking.status === 'requested');

  const accept = async () => {
    setBusy(true);
    setError('');
    try {
      const b = await api<Booking>('POST', `/bookings/${id}/accept`);
      onAnswered(b, `Accepted · ${b.productName}`, `${b.hotelName ?? 'The hotel'} has been told`);
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  const decline = async () => {
    if (!reason) return;
    setBusy(true);
    setError('');
    try {
      const b = await api<Booking>('POST', `/bookings/${id}/decline`, { reason, note: note || undefined });
      // Canon 11 F1 side-effect: also block the requested day (or, for
      // maintenance, the range) if the supplier asked. The decline has already
      // stuck; a block that clashes with another confirmed job (409) just leaves
      // that day open — not fatal. The range is a loop over the one-day endpoint.
      const cfg = DECLINE_BLOCKS[reason];
      let blocked = 0;
      let clashed = 0;
      if (blockDay && cfg && booking) {
        const startDay = booking.start.slice(0, 10);
        const days =
          cfg.range && blockThrough && blockThrough > startDay ? daysFromTo(startDay, blockThrough) : [startDay];
        for (const d of days) {
          try {
            await api('POST', `/availability/${d}/block`, { reason: cfg.reason, note: note || undefined });
            blocked += 1;
          } catch {
            clashed += 1; // a confirmed job that day — left open, cancel it to block
          }
        }
      }
      const sub =
        blockDay && cfg && booking
          ? clashed === 0
            ? blocked === 1
              ? `Reason sent · ${formatDay(booking.start.slice(0, 10))} now blocked`
              : `Reason sent · ${blocked} days blocked`
            : `Reason sent · blocked ${blocked}, ${clashed} had a confirmed job`
          : `${b.hotelName ?? 'The hotel'} gets your reason, not just a no`;
      onAnswered(b, `Declined · ${b.productName}`, sub);
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  const propose = async () => {
    const chosen = slot ?? manualSlot();
    if (!chosen) return;
    if (blockedDays.has(chosen.start.slice(0, 10))) {
      setError("You've blocked that day — pick a free one.");
      return;
    }
    setBusy(true);
    setError('');
    try {
      const b = await api<Booking>('POST', `/bookings/${id}/propose`, {
        start: chosen.start,
        end: chosen.end,
        note: note || undefined,
      });
      onAnswered(b, `Offered ${chosen.label} · ${b.productName}`, 'The slot is held for 12 hours');
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  // Their own FREE slots first, never a blank picker (canon 11 F2): candidate
  // days after the requested one, minus any the supplier has blocked. A slot
  // they can't actually do is never offered.
  const slots = booking ? suggestSlots(booking, blockedDays) : [];

  const manualSlot = () => {
    if (!manualDate || !booking) return null;
    const dur = Date.parse(booking.end) - Date.parse(booking.start);
    const offset = booking.start.slice(19);
    const start = `${manualDate}T${manualTime}:00${offset}`;
    const end = new Date(Date.parse(`${manualDate}T${manualTime}:00${offset}`) + dur);
    return { start, end: toLocalIso(end, offset), label: formatWhen(start) };
  };
  const chosen = slot ?? (manualDate ? manualSlot() : null);
  const chosenBlocked = chosen ? blockedDays.has(chosen.start.slice(0, 10)) : false;

  return (
    <>
      <div className="drawer-veil" onClick={close} />
      <aside className="drawer" role="dialog" aria-label="Request">
        <div className="scroll">
          {!booking ? (
            loadError ? <p className="error-line">{loadError}</p> : <Loading />
          ) : mode === 'answer' ? (
            <>
              {booking.chase?.state === 'escalated' ? (
                <span className="uchip escalated">
                  Escalated · answer by {booking.answerDeadline ? formatWhen(booking.answerDeadline) : 'now'}
                </span>
              ) : (
                <span className="uchip waiting">Needs answer · {ago(booking.createdAt)}</span>
              )}
              <h1 style={{ marginTop: 12 }}>{booking.productName}</h1>
              <p className="subline">{booking.hotelName}</p>
              <div className="kv">
                <div className="kvrow">
                  <span className="k">When</span>
                  <span className="v">{formatWhen(booking.start)}</span>
                </div>
                <div className="kvrow">
                  <span className="k">Guests</span>
                  <span className="v">
                    {booking.guests.adults} adults{booking.guests.children ? ` · ${booking.guests.children} children` : ''}
                  </span>
                </div>
                {booking.guestName && (
                  <div className="kvrow">
                    <span className="k">Guest</span>
                    <span className="v">{booking.guestName}</span>
                  </div>
                )}
                <div className="kvrow earn">
                  <span className="k">You earn</span>
                  <span className="v">{fmtMoney(booking.supplierAmount)}</span>
                </div>
              </div>
              {booking.notes && <p className="hint" style={{ marginBottom: 16 }}>“{booking.notes}”</p>}
              {!answerable && (
                <p>
                  Already answered: <StatusChip status={booking.status} />
                  {(() => {
                    const last = (booking.timeline ?? []).slice(-1)[0];
                    return last ? (
                      <span className="hint" style={{ display: 'block', marginTop: 4 }}>
                        by <span className="mono">{last.actor}</span>
                      </span>
                    ) : null;
                  })()}
                </p>
              )}
              {error && <p className="error-line" role="alert">{error}</p>}
            </>
          ) : mode === 'decline' ? (
            <>
              <h1>Why can't you?</h1>
              <p className="subline">The hotel gets your reason, not just a no — it is what lets them offer the guest something else.</p>
              {DECLINE_REASONS.map((r) => (
                <button
                  key={r.code}
                  className={`reason ${reason === r.code ? 'selected' : ''} ${r.code === 'other' ? 'muted' : ''}`}
                  onClick={() => { setReason(r.code); setBlockDay(false); setBlockThrough(''); }}
                  aria-pressed={reason === r.code}
                >
                  {r.label}
                  {reason === r.code && <span className="disc" />}
                </button>
              ))}
              {DECLINE_BLOCKS[reason] && (
                <>
                  <button
                    type="button"
                    className={`reason ${blockDay ? 'selected' : ''}`}
                    style={{ marginTop: 12 }}
                    onClick={() => setBlockDay((v) => !v)}
                  >
                    {DECLINE_BLOCKS[reason]!.label}
                    {blockDay && <span className="disc" />}
                  </button>
                  {DECLINE_BLOCKS[reason]!.range && blockDay && booking && (
                    <div className="field" style={{ marginTop: 8 }}>
                      <label htmlFor="through">Through — leave empty to block just {formatDay(booking.start.slice(0, 10))}</label>
                      <input
                        id="through"
                        type="date"
                        className="mono"
                        min={booking.start.slice(0, 10)}
                        value={blockThrough}
                        onChange={(e) => setBlockThrough(e.target.value)}
                      />
                    </div>
                  )}
                </>
              )}
              {/* Eva's catch (canon 11): a decline that should have been a
                  counter-offer is the one worth intercepting. */}
              {slots[0] && (
                <p className="hint" style={{ marginTop: 12, padding: '10px 12px', background: 'var(--color-primary-light)', borderRadius: 'var(--radius-lg)' }}>
                  Eva: you&rsquo;re free {slots[0].label}.{' '}
                  <button
                    type="button"
                    onClick={() => { setSlot(slots[0]!); setMode('propose'); }}
                    style={{ background: 'none', border: 'none', color: 'var(--text-link)', textDecoration: 'underline', cursor: 'pointer', padding: 0, font: 'inherit' }}
                  >
                    Offer that instead?
                  </button>
                </p>
              )}
              <div className="field" style={{ marginTop: 16 }}>
                <label htmlFor="note">A note for the hotel{reason === 'other' ? ' (required)' : ' (optional)'}</label>
                <textarea id="note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
              </div>
              {error && <p className="error-line" role="alert">{error}</p>}
            </>
          ) : (
            <>
              <h1>Offer another time</h1>
              <p className="subline">Your free slots first. Whatever you pick is held for 12 hours.</p>
              <div className="avail-label">You're free</div>
              {slots.map((s) => (
                <button key={s.start} className={`slot ${slot?.start === s.start ? 'selected' : ''}`} onClick={() => { setSlot(s); setManualDate(''); }}>
                  <div className="t">{s.label}</div>
                  <div className="s">{s.why}</div>
                </button>
              ))}
              {avail.data && slots.length === 0 && (
                <p className="hint">No free days in the next three weeks — pick a time below.</p>
              )}
              <div className="field" style={{ marginTop: 16 }}>
                <label>Pick another time…</label>
                <div className="row">
                  <input type="date" className="mono" value={manualDate} onChange={(e) => { setManualDate(e.target.value); setSlot(null); }} />
                  <input type="time" className="mono" value={manualTime} onChange={(e) => setManualTime(e.target.value)} />
                </div>
                {manualDate && blockedDays.has(manualDate) && (
                  <p className="error-line" role="alert" style={{ marginTop: 8 }}>You’ve blocked that day — the hotel would see you as unavailable.</p>
                )}
              </div>
              <div className="field">
                <label htmlFor="pnote">A line for the hotel — Eva drafted this, edit it freely</label>
                <textarea id="pnote" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
              </div>
              {error && <p className="error-line" role="alert">{error}</p>}
            </>
          )}
        </div>

        <div className="pinned">
          {!booking || !answerable ? (
            <button className="btn secondary" onClick={close} style={{ flex: 1 }}>
              Close
            </button>
          ) : mode === 'answer' ? (
            <div style={{ flex: 1 }}>
              <button className="btn lg" disabled={busy} onClick={accept} style={{ width: '100%', marginBottom: 8 }}>
                {busy ? 'Confirming…' : 'Accept'}
              </button>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn secondary" disabled={busy} onClick={() => setMode('propose')} style={{ flex: 1 }}>
                  Another time
                </button>
                <button className="btn danger-outline" disabled={busy} onClick={() => setMode('decline')} style={{ flex: 1 }}>
                  Can't do it
                </button>
              </div>
            </div>
          ) : mode === 'decline' ? (
            <>
              <button className="btn secondary" onClick={() => setMode('answer')} style={{ flex: 1 }}>
                Back
              </button>
              <button
                className="btn danger-solid"
                disabled={busy || !reason || (reason === 'other' && !note.trim())}
                onClick={decline}
                style={{ flex: 2 }}
              >
                {busy ? 'Sending…' : 'Send decline'}
              </button>
            </>
          ) : (
            <>
              <button className="btn secondary" onClick={() => setMode('answer')} style={{ flex: 1 }}>
                Back
              </button>
              <button className="btn" disabled={busy || !chosen || chosenBlocked} onClick={propose} style={{ flex: 2 }}>
                {busy ? 'Sending…' : chosenBlocked ? 'That day is blocked' : chosen ? `Offer ${chosen.label}` : 'Pick a slot'}
              </button>
            </>
          )}
        </div>
      </aside>
    </>
  );
}

function suggestSlots(b: Booking, blocked: Set<string>): { start: string; end: string; label: string; why: string }[] {
  const dur = Date.parse(b.end) - Date.parse(b.start);
  const offset = b.start.slice(19);
  const whys = ['Next free day · guests still in resort', 'The day after, same time', 'Soon after, same time'];
  const out: { start: string; end: string; label: string; why: string }[] = [];
  // Walk forward from the requested day, skip the supplier's blocked days, and
  // take the first three free ones at the same time — least guest movement
  // first (canon 11 F2). Never offers a day they can't do.
  for (let d = 1; d <= 21 && out.length < 3; d++) {
    const start = new Date(Date.parse(b.start) + d * 86_400_000);
    if (start.getTime() < Date.now()) continue;
    const s = toLocalIso(start, offset);
    if (blocked.has(s.slice(0, 10))) continue;
    const end = new Date(start.getTime() + dur);
    out.push({ start: s, end: toLocalIso(end, offset), label: formatWhen(s), why: whys[out.length] ?? 'Same time' });
  }
  return out;
}
